import { getAircraftType, getAircraftTypeName } from './aircraftType.js';
import { getAirlineName } from '../data/enrichment.js';

/**
 * Spotlight-Heuristik — kostenlose, token-freie Vorab-Einschätzung, ob ein
 * Flugzeug "besonders/cool" ist. Läuft vor dem KI-Screener (Haiku) und spart
 * damit Tokens:
 *
 *   - liefert ein Urteil { interesting:true, … } für offensichtliche Fälle
 *     (Rettung, Polizei, Militär, seltene/besondere Typen, Notfall-Squawk),
 *   - liefert { interesting:false } für offensichtlich Gewöhnliches
 *     (bekannte Airline + Verkehrsflugzeug, erkannter GA-/Segler-Typ),
 *   - liefert null für alles Unsichere → nur DAS geht an den KI-Screener.
 *
 * Bewusst konservativ: lieber „unsicher“ (null) als ein Fehlurteil. Die feineren
 * Fälle (Promi-/Regierungsjets, seltene Registrierungen) klärt Haiku, die Tiefe
 * das Opus-Dossier.
 *
 * @typedef {{ interesting:boolean, category:string, label:string, rarity:number, source:'heuristic' }} Verdict
 */

const v = (interesting, category, label, rarity) => ({
  interesting,
  category,
  label,
  rarity,
  source: 'heuristic'
});

// Rettungs-/Ambulanz-Hubschrauber (D-A-CH + generisch)
const RESCUE_CS = /^(CHX|CRISTOPH|CHRISTOPH|DRF|ADAC|HELI|AMB|SAR|RESCUE|REGA|NIGHTINGALE|PHEONIX|PHOENIX\d)/;

// Polizei
const POLICE_CS = /^(POLICE|POLIZEI|LES\d|SPHINX|PIPRA|HUMMEL|LIBELLE|EDELWEISS\d)/;

// Militär — Callsign-Präfixe (bewusst gut erkennbare, verbreitete)
const MIL_CS = /^(GAF|GAM|NATO|RCH|RRR|CTM|IAM|BAF|HAF|NOW\d|MMF|EGY\d?F|DUKE|ASCOT|GRZLY|MAGMA|TARTN|VIVI|BLKCT|SNAKE|VIKING|KING\d|CFC|PLF|FMY|LAGR|IRON|HOB|HERKY|POLICE\d)/;

// Militär — reine Militärtypen (ICAO-Designatoren)
const MIL_TYPES = new Set([
  'EUFI', 'RFAL', 'F16', 'F16C', 'F18', 'F18S', 'F15', 'F15E', 'F35', 'F35A',
  'A10', 'TOR', 'GR4', 'HAWK', 'HARR', 'AJET', 'MG29', 'SU25', 'SU27',
  'C17', 'C5M', 'C130', 'C30J', 'A400', 'K35R', 'KC10', 'KC30',
  'E3TF', 'E3CF', 'E3', 'E6', 'P8', 'P3', 'ATLA', 'RC13', 'U2',
  'B52', 'B1', 'B2', 'TU95', 'TU22', 'A124', 'AN12', 'AN22', 'AN26', 'AN30',
  'NH90', 'TIGR', 'H60', 'UH1', 'UH1Y', 'H53', 'H53S', 'CH47', 'V22', 'MERL'
].filter(Boolean));

// Notfall-Transpondercodes (Hijack / Funkausfall / allgemeiner Notfall)
const EMERGENCY_SQUAWK = new Set(['7500', '7600', '7700']);

// Seltene / besondere Typen rund um Stuttgart → als „Finding“ markieren
const RARE_TYPES = new Map([
  ['A124', v(true, 'special', '⭐ Antonov 124', 5)],
  ['AN12', v(true, 'special', '⭐ Antonov', 5)],
  ['AN22', v(true, 'special', '⭐ Antonov 22', 5)],
  ['B74S', v(true, 'special', '⭐ Dreamlifter', 5)],
  ['BLCF', v(true, 'special', '⭐ Dreamlifter', 5)],
  ['A3ST', v(true, 'special', '⭐ BelugaST', 4)],
  ['A337', v(true, 'special', '⭐ BelugaXL', 4)],
  ['A388', v(true, 'special', '⭐ A380', 3)],
  ['B748', v(true, 'special', '⭐ 747-8', 3)],
  ['B744', v(true, 'special', '⭐ 747-400', 3)],
  ['DC3', v(true, 'special', '🛩️ Oldtimer', 4)],
  ['C47', v(true, 'special', '🛩️ Oldtimer', 4)],
  ['JU52', v(true, 'special', '🛩️ Oldtimer', 5)],
  ['AN2', v(true, 'special', '🛩️ Oldtimer', 4)],
  ['YK52', v(true, 'special', '🛩️ Warbird', 4)],
  ['SPIT', v(true, 'special', '🛩️ Warbird', 5)],
  ['P51', v(true, 'special', '🛩️ Warbird', 5)],
  ['B17', v(true, 'special', '🛩️ Warbird', 5)],
  ['T6', v(true, 'special', '🛩️ Warbird', 4)]
]);

// Große Business-Jets: einen Blick wert (KI ergänzt evtl. Halter/Promi-Bezug)
const BIZJET_TYPES = new Set([
  'GLEX', 'GL5T', 'GL7T', 'GL6T', 'GLF4', 'GLF5', 'GLF6', 'G650', 'G550', 'G280',
  'FA7X', 'FA8X', 'F900', 'CL60', 'CL30', 'C700', 'C750'
]);

/**
 * Bewertet einen Flug rein lokal.
 * @param {Object} flight
 * @returns {Verdict|null} null = unsicher (an den KI-Screener geben)
 */
export function evaluateSpotlight(flight) {
  const cs = (flight.callsign || '').toUpperCase().trim();
  const type = (flight.aircraftType || '').toUpperCase().trim();
  const squawk = (flight.squawk || '').trim();

  // 1) Notfall-Squawk zuerst (höchste Priorität)
  if (EMERGENCY_SQUAWK.has(squawk)) return v(true, 'special', '🚨 Notfall', 5);

  // 2) Rettung / Polizei / Militär (klare Callsign-/Typ-Signale)
  if (RESCUE_CS.test(cs)) return v(true, 'rescue', '🚁 Rettung', 4);
  if (POLICE_CS.test(cs)) return v(true, 'police', '🚓 Polizei', 4);
  if (MIL_CS.test(cs) || MIL_TYPES.has(type)) return v(true, 'military', '🎖️ Militär', 5);

  // 3) Seltene / besondere Typen
  if (RARE_TYPES.has(type)) return RARE_TYPES.get(type);

  // 4) Große Privatjets — leicht interessant
  if (BIZJET_TYPES.has(type)) return v(true, 'business', '🛩️ Privatjet', 2);

  // 5) Offensichtlich gewöhnlich → NICHT an die KI geben (Tokens sparen)
  const klass = getAircraftType(flight);
  const airline = getAirlineName(cs);
  if (airline && klass === 'airplane') return v(false, 'airline', '', 1);
  // Erkannter (also gängiger) GA-/Segler-Typ: getAircraftTypeName liefert dann
  // einen echten Namen statt des rohen Codes zurück.
  if (type && (klass === 'smallplane' || klass === 'glider') &&
      getAircraftTypeName(type) !== type) {
    return v(false, 'ga', '', 1);
  }

  // 6) Unsicher → der KI-Screener (Haiku) entscheidet
  return null;
}
